<template>
    <div class="col_y" :style="styleString">
        <Cell
            v-for="(d, index) in emptyCells"
            :key="index"
            :timeData="d"
            :column="column"
            :selectedAreaHelper="selectedAreaHelper"
            :xPosition="xPosition"
            :originalBlocksAndColors="originalBlocksAndColors"
            :blockOrColor="reservationBlocksAndColors[d.timeString]"
            :workCode="workCodes[d.timeString]"
            :boldLineTimes="boldLineTimes"
        />

        <ReservationCell
            v-for="(r, index) in filteredReservations"
            :key="r._id + index"
            :reservation="r"
            :maxDepth="maxDepth"
            :hoverReservation="hoverReservation"
            :selectedAreaHelper="selectedAreaHelper"
            @action_ReservationMouseOver="handleReservationMouseOver"
        />
    </div>
</template>

<script>
import { deepCopy } from '@/util/deep-copy'
import Cell from './DoctorColumnCell.vue'
import ReservationCell from '@/v2/views/Main/V2ReservationManagement/Components/Sheet/Column/ReservationCell.vue'
import { sheetAxis } from '../../../sheet-axis'
import { sheetFilters, selectedColumns, selectedStates } from '../../../sheet-filters'
import { sheetRefresh } from '../../../sheet-refresh'
import { columnWidth } from './column-width'
import ReservationPositionCalculator from '../../../ReservationPositionCalculator'

import moment from '@/util/MPMoment'
import { mapGetters } from 'vuex'
import store from '@/store'
import { useV2ReservationListQuery } from '@/composables/queries/v2reservation/use-v2-reservation-list-query'

export default {
    components: { ReservationCell, Cell },
    props: {
        column: Object,
        selectedAreaHelper: Object,
        hoverReservation: Object,
        xPosition: Number,
        originalWorkRecords: Array,
        originalBlocksAndColors: Array
    },
    setup(props) {
        // getter(지연 평가) — vue-query 내부 computed 안에서 호출돼 최신값 읽기 + 반응성 추적이 이뤄진다
        const params = () => {
            const selectedDate = sheetAxis.selectedDate ?? undefined
            return {
                hospitalID: store.state.hospital.selectedHospital?._id,
                groupID: props.column?.groupID?._id,
                columnID: props.column?._id,
                isRemoved: false,
                // 기존 requestFetchReservations 와 동일한 포맷(moment.toString())을 유지한다
                startedAt: moment(selectedDate).startOf('days').toString(),
                endedAt: moment(selectedDate).endOf('days').toString()
            }
        }

        const options = () => ({ enabled: !!props.column?._id })

        const {
            data: reservationData,
            error: reservationError,
            refetch: refetchReservations
        } = useV2ReservationListQuery({ params, options })

        return { reservationData, reservationError, refetchReservations }
    },
    data() {
        return {
            sheetAxis,
            sheetFilters,
            sheetRefresh,
            calculator: undefined,
            maxDepth: undefined,

            targetDateString: '',
            reservations: [],
            reservationBlocksAndColors: [],
            workCodes: [],
            boldLineTimes: [],

            originalReservations: [],

            columnWidth
        }
    },
    watch: {
        'sheetRefresh.refreshFlagMoment': {
            deep: true,
            handler: function () {
                if (this.selectedDateString != this.targetDateString) {
                    // reservations 는 비우지 않는다 — 날짜가 바뀌면 쿼리 키가 바뀌어
                    // reservationData 가 undefined→새값으로 흐르며 watch 가 비우고 채운다.
                    // 여기서 비우면 동일-데이터 재조회(structural sharing, 같은 참조 유지) 시
                    // watch 가 발화하지 않아 빈 화면으로 남는다.
                    this.reservationBlocksAndColors = []
                    this.workCodes = []
                    this.targetDateString = this.selectedDateString
                    // 날짜 변경은 쿼리 키 변경이 조회를 담당한다 (5분 내 재방문이면 캐시 사용).
                    // 여기서 refetch(강제 조회)를 걸면 staleTime 캐시가 무력화된다.
                    this.computedBoldLineTimes()
                    return
                }
                this.targetDateString = this.selectedDateString
                // 날짜가 같을 때의 refresh 는 명시적 갱신 신호(수동 새로고침·저장 후) — 강제 재조회 유지
                this.refetchReservations()
                this.computedBoldLineTimes()
            }
        },
        'sheetRefresh.reservationRefreshEvent': {
            handler: function (newValue) {
                if (!newValue) {
                    return
                }

                const isMine = (newValue.columnIDs ?? []).some(id => id == this.columnID)

                // 본인의 열이거나, 또는 전체열 조회 조건인 경우만 실행.
                if (!newValue.isAllColumns && !isMine) {
                    return
                }

                if (this.selectedDateString != this.targetDateString) {
                    // reservations 는 비우지 않는다 (위 refreshFlagMoment 워처와 동일한 이유)
                    this.reservationBlocksAndColors = []
                    this.workCodes = []
                    this.targetDateString = this.selectedDateString
                    // 날짜 변경 직후의 이벤트 — 키 변경 조회 + invalidation 이 최신성을 보장하므로 강제 refetch 생략
                    this.computedBoldLineTimes()
                    return
                }

                this.targetDateString = this.selectedDateString
                this.refetchReservations()
                this.computedBoldLineTimes()
            }
        },
        // TanStack Query 데이터 → 기존 위치 계산 파이프라인 연결
        // immediate: remount 시(담당자 필터 해제 등) 캐시에 이미 들어있는 값으로도 즉시 렌더
        reservationData: {
            immediate: true,
            handler: function (newData) {
                this.originalReservations = (newData ?? []).filter(w => w.statusID?.isRemoved == false)
                this.runAlgorithm()
            }
        },
        reservationError: {
            handler: function (err) {
                if (err) {
                    console.log('Error Fetching reservation list: ', err)
                    this.reservations = []
                }
            }
        },
        columnID: {
            handler: function (newValue, oldValue) {
                if (newValue == oldValue) {
                    return
                }
                this.initialData()
            }
        },
        originalWorkRecords: {
            handler: function () {
                this.computedEachCellWorkCode()
            }
        },
        blocksAndColors: {
            handler: function () {
                this.computedEachCellBlocksAndColors()
            }
        },
        emptyCells: {
            handler: function (newValue, oldValue) {
                if (!newValue) {
                    return
                }
                let n = moment(newValue?.[0]?.time).format('YYYY-MM-DD')
                let o = moment(oldValue?.[0]?.time).format('YYYY-MM-DD')
                if (n == o) {
                    if (newValue.length != oldValue.length) {
                        this.computedEachCellBlocksAndColors()
                        this.computedEachCellWorkCode()
                        this.computedBoldLineTimes()
                    }
                }
            }
        },
        selectedStates: {
            handler: function () {
                this.runAlgorithm(this.reservations)
            }
        }
    },
    computed: {
        ...mapGetters(['CellConfig_FindColumnWidth']),
        styleString: function () {
            return `width: ${this.width}px;`
        },
        storedWidthArray: function () {
            return this.columnWidth.storedWidths
        },
        widthObject: function () {
            return this.storedWidthArray.find((w) => w._id == this.column._id)
        },
        width: function () {
            return this.widthObject?.width ?? 160
        },
        groupID: function () {
            return this.column.groupID?._id
        },
        columnID: function () {
            return this.column._id
        },
        employeeID: function () {
            return this.column.employeeID
        },
        boldInterval: function () {
            return this.column?.boldInterval || 60
        },
        boldTimeIntervals: function () {
            return this.column?.boldTimeIntervals || []
        },
        emptyCells: function () {
            return this.sheetAxis.timeArray
        },
        selectedDate: function () {
            return this.sheetAxis.selectedDate ?? undefined
        },
        selectedDateString: function () {
            return moment(this.selectedDate).format('YYYY-MM-DD')
        },
        timeInterval: function () {
            return this.sheetAxis.selectedTimeInterval
        },
        stateIDs: function () {
            return this.sheetFilters.states.filter((c) => c.isSelected).map((c) => c._id)
        },
        sheetStartTime: function () {
            return moment(this.sheetAxis.sheetStartedAt)
        },
        blocksAndColors: function () {
            return this.originalBlocksAndColors.filter((c) => c.columns.some((id) => id == this.columnID))
        },
        selectedStates: function () {
            return selectedStates()
        },
        filteredReservations() {
            if (selectedColumns().length === 0) {
                return []
            }
            return this.reservations.filter((r) => this.selectedStates.some((s) => s._id === r.statusID._id))
        }
    },
    mounted() {
        this.initialData()
    },
    methods: {
        // MARK: - Private
        async runAlgorithm() {
            try {
                this.calculator = new ReservationPositionCalculator()
                this.calculator.reservations = this.originalReservations.filter((r) => this.selectedStates.some((s) => s._id === r.statusID._id))
                this.calculator.startTime = moment(this.selectedDate).set({
                    hours: this.sheetStartTime.hour(),
                    minutes: this.sheetStartTime.minutes()
                })

                const result = await this.calculator.run()
                this.reservations = result
                this.$emit('update-reservations', result)
                this.maxDepth = this.calculator.calculatedData.length
            } catch (err) {
                throw new Error(err)
            }
        },

        initialData() {
            // 그려진 날짜 스냅샷을 현재 날짜로 앵커링 — 비워두면 첫 refresh 이벤트가
            // 날짜 변경으로 오판되어 재조회를 생략한다
            this.targetDateString = this.selectedDateString
            // 예약 조회는 setup 의 useV2ReservationListQuery 가 파라미터 키 기준으로 자동 수행한다
            this.reservations = []
            this.computedEachCellBlocksAndColors()
            this.computedEachCellWorkCode()
            this.computedBoldLineTimes()
        },

        computedBoldLineTimes() {
            if (!this.columnID) {
                this.boldLineTimes = []
                return
            }
            if (this.emptyCells.length > 0) {
                let start = this.timeToMinutes(this.emptyCells[0].timeString)

                this.boldLineTimes = this.emptyCells.reduce((acc, time, index) => {
                    if (index == 0) {
                        return acc
                    }
                    if (this.boldTimeIntervals.length > 0) {
                        if (this.boldTimeIntervals.some((t) => t == time.timeString)) {
                            acc.push(time.timeString)
                        }
                    } else {
                        let target = this.timeToMinutes(time.timeString)
                        if ((target - start) % this.boldInterval == 0) {
                            acc.push(time.timeString)
                        }
                    }
                    return acc
                }, [])
            }
        },

        timeToMinutes(timeString) {
            let [hour, minute] = timeString.split(':').map(Number)
            return hour * 60 + minute
        },

        computedEachCellBlocksAndColors() {
            try {
                if (!this.columnID) {
                    this.reservationBlocksAndColors = []
                    return
                }
                if (this.blocksAndColors.length >= 0) {
                    let bc = deepCopy(this.blocksAndColors)
                    this.reservationBlocksAndColors = this.emptyCells.reduce((acc, t) => {
                        let targetTime = t.timeString
                        let targetStartMinutes = this.timeToMinutes(targetTime)
                        let targetEndMinutes = targetStartMinutes + this.timeInterval

                        let data = bc.find((d) => {
                            let startMinutes = this.timeToMinutes(moment(d.startedAt).format('HH:mm'))
                            let endMinutes = this.timeToMinutes(moment(d.endedAt).format('HH:mm'))

                            return targetStartMinutes < endMinutes && targetEndMinutes > startMinutes
                        })
                        if (data) {
                            data.isFirstIndex = data.isFirstIndex == undefined
                            acc[t.timeString] = { ...data }
                        }
                        return acc
                    }, {})
                }
            } catch {
                this.reservationBlocksAndColors = []
            }
        },

        parseWorkRecordData(records) {
            let employeeTitle
            let workRecord = records.find(
                (record) =>
                    record.employees?.some((e) => {
                        if (e._id == this.employeeID) {
                            employeeTitle = e.title
                            return true
                        }
                        return false
                    }) ?? false
            )

            if (workRecord?.isCustomized) {
                return {
                    isCustomized: true,
                    employeeTitle: employeeTitle,
                    startedAt: workRecord.startedAt,
                    endedAt: workRecord.endedAt
                }
            } else {
                let workCode = deepCopy(workRecord?.workCodeID)
                if (workCode) {
                    workCode.employeeTitle = employeeTitle
                }
                return workCode
            }
        },

        computedEachCellWorkCode() {
            try {
                this.workCodes = []

                if (!this.columnID || !this.employeeID) {
                    return
                }
                let workCode = this.parseWorkRecordData(this.originalWorkRecords)

                if (workCode != undefined) {
                    this.workCodes = this.emptyCells.reduce((acc, t) => {
                        if (workCode.isDefault) {
                            workCode.isFirstIndex = workCode.isFirstIndex == undefined
                            acc[t.timeString] = { ...workCode }
                        } else {
                            let targetTime = t.timeString
                            let [targetHour, targetMinute] = targetTime.split(':').map(Number)
                            let targetEndHour = targetHour + Math.floor((targetMinute + this.timeInterval) / 60)
                            let targetEndMinute = (targetMinute + this.timeInterval) % 60

                            let setMomentTime = (time) => {
                                return moment(this.selectedDate)
                                    .clone()
                                    .set({ hour: moment(time).hour(), minute: moment(time).minute() })
                            }
                            let startedAt = setMomentTime(workCode.startedAt)
                            let endedAt = setMomentTime(workCode.endedAt)

                            let startHour = parseInt(startedAt.format('HH:mm').split(':')[0])
                            let startMinute = parseInt(startedAt.format('HH:mm').split(':')[1])
                            let endHour = parseInt(endedAt.format('HH:mm').split(':')[0])
                            let endMinute = parseInt(endedAt.format('HH:mm').split(':')[1])

                            let isOutsideWorkCode =
                                targetEndHour < startHour ||
                                (targetEndHour == startHour && targetEndMinute <= startMinute) ||
                                targetHour > endHour ||
                                (targetHour == endHour && targetMinute >= endMinute)
                            if (isOutsideWorkCode) {
                                workCode.isFirstIndex = workCode.isFirstIndex == undefined
                                acc[t.timeString] = { ...workCode }
                            }
                        }
                        return acc
                    }, {})
                }
            } catch {
                this.workCodes = []
            }
        },

        // MARK: - Action
        handleReservationMouseOver(reservation, hoverEvent, isShow) {
            this.$emit('action_ReservationMouseOver', reservation, hoverEvent, isShow)
        }
    }
}
</script>
